import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AccessGrant, LoginChallenge, RegistrationGrant } from "@anyseal/contracts";
import { portalClient } from "./api";

type AuthStatus = "initializing" | "authenticated" | "anonymous";
type LoginResult = { challenge?: LoginChallenge };
type RegistrationInput = {
  email: string;
  password: string;
  display_name: string;
  locale: "zh-CN" | "en";
  organization_display_name: string;
  default_currency: "CNY" | "USD";
  referral_code?: string;
};
type AuthValue = {
  status: AuthStatus;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeTwoFactor: (flowToken: string, code: string) => Promise<void>;
  completeOAuth: (flowToken: string) => Promise<void>;
  register: (input: RegistrationInput) => Promise<RegistrationGrant>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("initializing");
  useEffect(() => {
    let active = true;
    portalClient.restoreSession().then((restored) => { if (active) setStatus(restored ? "authenticated" : "anonymous"); });
    const expired = () => setStatus("anonymous");
    window.addEventListener("anyseal:portal-auth-expired", expired);
    return () => { active = false; window.removeEventListener("anyseal:portal-auth-expired", expired); };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const result = await portalClient.post<AccessGrant | LoginChallenge>("/api/portal/v1/auth/login", { email, password }, { authenticated: false });
    if ("challenge" in result) return { challenge: result };
    portalClient.setAccessToken(result.access_token);
    setStatus("authenticated");
    return {};
  }, []);

  const completeTwoFactor = useCallback(async (flowToken: string, code: string) => {
    const result = await portalClient.post<AccessGrant>("/api/portal/v1/auth/two-factor/complete", { flow_token: flowToken, code }, { authenticated: false });
    portalClient.setAccessToken(result.access_token);
    setStatus("authenticated");
  }, []);

  const completeOAuth = useCallback(async (flowToken: string) => {
    const result = await portalClient.post<AccessGrant>("/api/portal/v1/auth/oauth/complete", { flow_token: flowToken }, { authenticated: false });
    portalClient.setAccessToken(result.access_token);
    setStatus("authenticated");
  }, []);

  const register = useCallback(async (input: RegistrationInput) => {
    const result = await portalClient.post<RegistrationGrant>("/api/portal/v1/auth/register", input, { authenticated: false });
    portalClient.setAccessToken(result.access_token);
    setStatus("authenticated");
    return result;
  }, []);

  const logout = useCallback(async () => {
    await portalClient.logout();
    setStatus("anonymous");
  }, []);

  const value = useMemo<AuthValue>(() => ({ status, login, completeTwoFactor, completeOAuth, register, logout }), [status, login, completeTwoFactor, completeOAuth, register, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is missing");
  return value;
}
